import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Link2,
  FileText,
  Image as ImageIcon,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Activity,
  Loader2,
} from 'lucide-react';
import {
  FaInstagram, FaXTwitter, FaLinkedin, FaFacebook,
  FaTiktok, FaYoutube, FaPinterest, FaThreads, FaBluesky,
} from 'react-icons/fa6';
import { api } from '../lib/api';

/* ── Types ──────────────────────────────────────────────── */
type Stats = {
  users: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
    registrationsLast14: { date: string; count: number }[];
  };
  socialAccounts: {
    total: number;
    connected: number;
    byPlatform: { platform: string; count: number }[];
  };
  posts: {
    draft: number;
    scheduled: number;
    publishing: number;
    published: number;
    partialFailed: number;
    failed: number;
    canceled: number;
    total: number;
  };
  media: {
    total: number;
    images: number;
    videos: number;
  };
};

type AdminUser = {
  id: string;
  email: string;
  role: 'user' | 'admin';
  createdAt: string;
  lastLoginAt: string | null;
};

const PLATFORM_ICONS: Record<string, { icon: typeof FaInstagram; color: string }> = {
  instagram:       { icon: FaInstagram, color: '#E4405F' },
  twitter:         { icon: FaXTwitter,  color: '#0F1419' },
  linkedin:        { icon: FaLinkedin,  color: '#0A66C2' },
  facebook:        { icon: FaFacebook,  color: '#1877F2' },
  tiktok:          { icon: FaTiktok,    color: '#010101' },
  tiktok_business: { icon: FaTiktok,    color: '#010101' },
  youtube:         { icon: FaYoutube,   color: '#FF0000' },
  pinterest:       { icon: FaPinterest, color: '#BD081C' },
  threads:         { icon: FaThreads,   color: '#000000' },
  bluesky:         { icon: FaBluesky,   color: '#0085FF' },
};

/* ── Main page ──────────────────────────────────────────── */
export function AdminDashboard() {
  const statsQuery = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn:  () => api<Stats>('/api/admin/stats'),
    refetchInterval: 30_000,
  });
  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn:  () => api<{ users: AdminUser[] }>('/api/admin/users'),
  });

  const s = statsQuery.data;
  const recentUsers = (usersQuery.data?.users ?? []).slice(0, 8);

  return (
    <div className="space-y-8">
      {/* header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          Vista general en tiempo real · actualización cada 30 s
        </p>
      </div>

      {/* ── KPI row ─────────────────────────────────────── */}
      {statsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8">
          <Loader2 size={16} className="animate-spin" /> Cargando estadísticas…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={<Users size={20} />}
              label="Usuarios totales"
              value={s?.users.total ?? 0}
              sub={`+${s?.users.today ?? 0} hoy · +${s?.users.thisWeek ?? 0} esta semana`}
              color="brand"
            />
            <KpiCard
              icon={<Link2 size={20} />}
              label="Cuentas sociales"
              value={s?.socialAccounts.connected ?? 0}
              sub={`${s?.socialAccounts.total ?? 0} total (incluye desconectadas)`}
              color="violet"
            />
            <KpiCard
              icon={<FileText size={20} />}
              label="Posts publicados"
              value={s?.posts.published ?? 0}
              sub={`${s?.posts.scheduled ?? 0} programados · ${s?.posts.draft ?? 0} borradores`}
              color="emerald"
            />
            <KpiCard
              icon={<ImageIcon size={20} />}
              label="Archivos media"
              value={s?.media.total ?? 0}
              sub={`${s?.media.images ?? 0} imágenes · ${s?.media.videos ?? 0} vídeos`}
              color="amber"
            />
          </div>

          {/* ── Second row ─────────────────────────────── */}
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Registrations sparkbar (14 days) */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-semibold text-slate-800">Registros</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Últimos 14 días</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                  <TrendingUp size={14} /> +{s?.users.thisMonth ?? 0} este mes
                </div>
              </div>
              <SparkBars data={s?.users.registrationsLast14 ?? []} />
            </div>

            {/* Posts by status */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h2 className="font-semibold text-slate-800 mb-4">Estado de posts</h2>
              <div className="space-y-2.5">
                <StatusRow icon={<CheckCircle2 size={15} className="text-emerald-500" />} label="Publicados"   value={s?.posts.published      ?? 0} total={s?.posts.total ?? 1} color="bg-emerald-500" />
                <StatusRow icon={<Clock         size={15} className="text-brand-500" />}   label="Programados" value={s?.posts.scheduled      ?? 0} total={s?.posts.total ?? 1} color="bg-brand-500" />
                <StatusRow icon={<FileText      size={15} className="text-slate-400" />}   label="Borradores"  value={s?.posts.draft          ?? 0} total={s?.posts.total ?? 1} color="bg-slate-400" />
                <StatusRow icon={<Activity      size={15} className="text-yellow-500" />}  label="Publicando"  value={s?.posts.publishing     ?? 0} total={s?.posts.total ?? 1} color="bg-yellow-400" />
                <StatusRow icon={<AlertTriangle size={15} className="text-orange-500" />}  label="Parcial"     value={s?.posts.partialFailed  ?? 0} total={s?.posts.total ?? 1} color="bg-orange-400" />
                <StatusRow icon={<XCircle       size={15} className="text-red-500" />}      label="Fallidos"    value={s?.posts.failed         ?? 0} total={s?.posts.total ?? 1} color="bg-red-500" />
              </div>
            </div>
          </div>

          {/* ── Third row ──────────────────────────────── */}
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Recent users */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-semibold text-slate-800">Últimos registros</h2>
                <a href="/admin/users" className="text-xs text-brand-600 hover:text-brand-700">Ver todos →</a>
              </div>
              {usersQuery.isLoading ? (
                <div className="p-6 text-slate-400 text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Cargando…
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Email</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden sm:table-cell">Rol</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsers.map((u, i) => (
                      <tr key={u.id} className={i < recentUsers.length - 1 ? 'border-b border-slate-50' : ''}>
                        <td className="px-5 py-3 text-slate-700 truncate max-w-[200px]">{u.email}</td>
                        <td className="px-5 py-3 hidden sm:table-cell">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            u.role === 'admin'
                              ? 'bg-brand-100 text-brand-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-slate-400 text-xs">{relativeTime(u.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Social accounts by platform */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h2 className="font-semibold text-slate-800 mb-4">Redes conectadas</h2>
              {(s?.socialAccounts.byPlatform.length ?? 0) === 0 ? (
                <p className="text-sm text-slate-400">Sin cuentas conectadas aún.</p>
              ) : (
                <div className="space-y-2.5">
                  {s?.socialAccounts.byPlatform.map((p) => {
                    const meta = PLATFORM_ICONS[p.platform];
                    if (!meta) return null;
                    const Icon = meta.icon;
                    const maxCount = s.socialAccounts.byPlatform[0]?.count ?? 1;
                    return (
                      <div key={p.platform} className="flex items-center gap-3">
                        <Icon size={16} color={meta.color} className="shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-500 capitalize">{p.platform.replace('_', ' ')}</span>
                            <span className="text-slate-700 font-medium">{p.count}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-brand-500"
                              style={{ width: `${Math.round((p.count / maxCount) * 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────── */

function KpiCard({
  icon, label, value, sub, color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
  color: 'brand' | 'emerald' | 'violet' | 'amber';
}) {
  const iconBg = {
    brand:   'bg-brand-50 text-brand-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    violet:  'bg-violet-50 text-violet-600',
    amber:   'bg-amber-50 text-amber-600',
  }[color];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${iconBg}`}>
        {icon}
      </div>
      <div className="mt-3 text-3xl font-extrabold text-slate-900 leading-none">
        {value.toLocaleString('es-ES')}
      </div>
      <div className="mt-1.5 text-sm font-medium text-slate-700">{label}</div>
      <div className="mt-1 text-xs text-slate-400 leading-snug">{sub}</div>
    </div>
  );
}

function StatusRow({
  icon, label, value, total, color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-500">{label}</span>
          <span className="text-slate-700 font-medium tabular-nums">{value}</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function SparkBars({ data }: { data: { date: string; count: number }[] }) {
  if (data.length === 0) {
    return <div className="h-24 flex items-center justify-center text-slate-400 text-sm">Sin datos</div>;
  }
  const max = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="flex items-end gap-1 h-24">
      {data.map((d) => {
        const heightPct = Math.max((d.count / max) * 100, d.count > 0 ? 8 : 2);
        const label = new Date(d.date + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
        return (
          <div
            key={d.date}
            className="flex-1 flex flex-col items-center justify-end gap-1 group relative"
            title={`${label}: ${d.count} registro${d.count !== 1 ? 's' : ''}`}
          >
            <div
              className={`w-full rounded-t transition-all ${d.count > 0 ? 'bg-brand-500 group-hover:bg-brand-400' : 'bg-slate-100'}`}
              style={{ height: `${heightPct}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 1)  return 'ahora mismo';
  if (mins  < 60) return `hace ${mins} min`;
  if (hours < 24) return `hace ${hours} h`;
  if (days  < 30) return `hace ${days} d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}
